import React, { useReducer, useMemo, ReactNode } from "react";
import {
  DocumentsContext,
  documentsReducer,
  IDocumentContext,
} from "./DocumentsContext";
import { Language } from "../Constants/LanguageConstants";
import {
  DocumentManifestModel,
  IDocumentModel,
  InitManifestsModel,
} from "../Models/DocumentManifestModel";
import { SettingsModel } from "../Models/SettingsModel";

interface DocumentsProviderProps {
  children: ReactNode;
}

export const DocumentsProvider: React.FC<DocumentsProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(documentsReducer, {
    documents: [],
    selectedDocumentFile: null,
    loading: false,
    error: null,
    searchTerm: "",
  });

  // Computed values
  const filteredDocuments = useMemo(() => {
    if (!state.searchTerm) return state.documents;
    return state.documents.filter((doc) =>
      doc.file.toLowerCase().includes(state.searchTerm.toLowerCase())
    );
  }, [state.documents, state.searchTerm]);

  const selectedDocument = useMemo(() => {
    if (!state.selectedDocumentFile) return null;
    return (
      state.documents.find((doc) => doc.file === state.selectedDocumentFile) ||
      null
    );
  }, [state.documents, state.selectedDocumentFile]);
  // Helper functions
  const initializeDocuments = async (lang: Language) => {
    dispatch({ type: "SET_LOADING", payload: true });

    try {
      const initSuccess = await InitManifestsModel(lang);

      if (!initSuccess) {
        dispatch({
          type: "SET_ERROR",
          payload: "Failed to load document manifests",
        });
        return;
      }

      console.log("Loading documents:", DocumentManifestModel.documents.length);

      // Fix: Use Promise.all to wait for ALL async operations
      const documentPromises = DocumentManifestModel.documents.map(
        async (doc) => {
          try {
            const text = await fetchMarkdownContent(
              `${SettingsModel.documentUri}${lang}/${doc.file}`
            );
            console.log(`Loaded document: ${doc.file}, length: ${text.length}`);
            return { ...doc, content: text };
          } catch (error) {
            console.error(`Failed to fetch document ${doc.file}:`, error);
            // Return document with empty content instead of breaking everything
            return { ...doc, content: "" };
          }
        }
      );

      // Wait for ALL documents to load
      const docs = await Promise.all(documentPromises);

      // Filter out documents that failed to load (if you want)
      const validDocs = docs.filter((doc) => doc.content.length > 0);

      console.log(`Successfully loaded ${validDocs.length} documents`);

      // CRITICAL: Actually dispatch the documents!
      dispatch({ type: "SET_DOCUMENTS", payload: validDocs });
    } catch (error) {
      console.error("Error initializing documents:", error);
      dispatch({
        type: "SET_ERROR",
        payload:
          error instanceof Error
            ? error.message
            : "Failed to initialize documents",
      });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const selectDocument = (id: string | null) => {
    dispatch({ type: "SELECT_DOCUMENT", payload: id });
  };

  const addDocument = (document: IDocumentModel) => {
    dispatch({ type: "ADD_DOCUMENT", payload: document });
  };

  const removeDocument = (id: string) => {
    dispatch({ type: "REMOVE_DOCUMENT", payload: id });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  };

  const search = (term: string) => {
    dispatch({ type: "SET_SEARCH_TERM", payload: term });
  };

  // Function to fetch markdown content
  const fetchMarkdownContent = async (filePath: string): Promise<string> => {
    try {
      const normalizedPath = `${process.env.PUBLIC_URL}/${filePath.replace(/^\//, "")}`;

      console.log("Fetching markdown from path:", normalizedPath);

      const response = await fetch(normalizedPath);

      console.log("Response status:", response.status);
      console.log("Response URL:", response.url);
      console.log("Response headers:", response.headers.get("content-type"));

      if (!response.ok) {
        throw new Error(
          `Failed to fetch markdown: ${response.status} ${response.statusText}`
        );
      }

      // Check if we're getting HTML instead of markdown
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("text/html")) {
        throw new Error(
          `Expected markdown file but got HTML. Check if file exists at: ${normalizedPath}`
        );
      }

      // Extract text content from the Response object
      const markdownText = await response.text();

      // Additional check: if content starts with DOCTYPE, it's HTML
      if (markdownText.trim().startsWith("<!DOCTYPE html>")) {
        throw new Error(
          `File not found. React dev server returned index.html instead of markdown file at: ${normalizedPath}`
        );
      }

      console.log(
        "Successfully fetched markdown, length:",
        markdownText.length
      );
      return markdownText;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch markdown content";
      console.error("Error fetching markdown:", error);
      throw new Error(errorMessage);
    }
  };

  const contextValue: IDocumentContext = {
    state,
    dispatch,
    filteredDocuments,
    selectedDocument,
    initializeDocuments,
    selectDocument,
    addDocument,
    removeDocument,
    setLoading,
    setError,
    search,
    fetchMarkdownContent,
  };

  return (
    <DocumentsContext.Provider value={contextValue}>
      {children}
    </DocumentsContext.Provider>
  );
};

export default DocumentsProvider;
